import React, { useState, useEffect } from 'react';
import { apiClient } from '../../apiClient';

const SystemSettings = () => {
    const [trialSettings, setTrialSettings] = useState({
        trialEnabled: false,
        trialStartDate: '',
        trialEndDate: '',
        trialMonths: 1
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    // Formats a Date object into YYYY-MM-DDTHH:mm in JST
    const toJSTDateTimeString = (date) => {
        if (!date || isNaN(date.getTime())) return '';
        const formatter = new Intl.DateTimeFormat('ja-JP', {
            timeZone: 'Asia/Tokyo',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        const parts = formatter.formatToParts(date);
        const getPart = (type) => parts.find(p => p.type === type).value;
        return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}`;
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await apiClient.get('/v1/admin/system-settings/trial');
                if (response.ok) {
                    const data = await response.json();
                    setTrialSettings({
                        ...data,
                        trialStartDate: data.trialStartDate ? toJSTDateTimeString(new Date(data.trialStartDate._seconds * 1000)) : '',
                        trialEndDate: data.trialEndDate ? toJSTDateTimeString(new Date(data.trialEndDate._seconds * 1000)) : ''
                    });
                }
            } catch (error) {
                console.error("Error fetching trial settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSaveTrial = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            const convertToUTC = (dateTimeString) => {
                if (!dateTimeString) return null;
                // Treat datetime string as JST
                return new Date(`${dateTimeString}:00+09:00`).toISOString();
            };

            const payload = {
                ...trialSettings,
                trialStartDate: convertToUTC(trialSettings.trialStartDate),
                trialEndDate: convertToUTC(trialSettings.trialEndDate),
            };

            const response = await apiClient.post('/v1/admin/system-settings/trial', payload);
            if (response.ok) {
                setMessage('トライアル設定を保存しました。');
            } else {
                setMessage('保存に失敗しました。');
            }
        } catch (error) {
            console.error("Error saving trial settings:", error);
            setMessage('エラーが発生しました。');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8">読み込み中...</div>;

    return (
        <div className="container mx-auto px-4 py-8">
            <h2 className="text-2xl font-bold mb-6">トライアル設定</h2>

            {message && (
                <div className={`p-4 mb-4 rounded ${message.includes('失敗') || message.includes('エラー') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {message}
                </div>
            )}

            <div className="bg-white shadow rounded-lg p-6 max-w-2xl">
                <form onSubmit={handleSaveTrial} className="space-y-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="trialEnabled"
                            checked={trialSettings.trialEnabled}
                            onChange={(e) => setTrialSettings({ ...trialSettings, trialEnabled: e.target.checked })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="trialEnabled" className="ml-2 block text-sm text-gray-900 font-bold">
                            トライアル受付を有効にする
                        </label>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">受付開始日時 (JST)</label>
                        <input
                            type="datetime-local"
                            value={trialSettings.trialStartDate}
                            onChange={(e) => setTrialSettings({ ...trialSettings, trialStartDate: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">受付終了日時 (JST)</label>
                        <input
                            type="datetime-local"
                            value={trialSettings.trialEndDate}
                            onChange={(e) => setTrialSettings({ ...trialSettings, trialEndDate: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">トライアル有効月数</label>
                        <input
                            type="number"
                            min="1"
                            value={trialSettings.trialMonths}
                            onChange={(e) => setTrialSettings({ ...trialSettings, trialMonths: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-gray-400 font-bold"
                    >
                        {saving ? '保存中...' : 'トライアル設定を保存'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SystemSettings;
